/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { EuiPopover } from '../../../popover';
import { formatTimeString } from '../pretty_duration';
import { EuiDatePopoverContent } from './date_popover_content';
export var EuiDatePopoverButton = props => {
  const {
    position,
    isDisabled,
    isInvalid,
    needsUpdating,
    value,
    buttonProps,
    roundUp,
    onChange,
    locale,
    dateFormat,
    utcOffset,
    timeFormat,
    isOpen,
    onPopoverToggle,
    onPopoverClose,
    ...rest
  } = props;
  const classes = classNames(['euiDatePopoverButton', `euiDatePopoverButton--${position}`, {
    'euiDatePopoverButton-isSelected': isOpen,
    'euiDatePopoverButton-isInvalid': isInvalid,
    'euiDatePopoverButton-needsUpdating': needsUpdating,
    'euiDatePopoverButton-disabled': isDisabled
  }]);
  let title = value;

  if (isInvalid) {
    title = `Invalid date: ${title}`;
  } else if (needsUpdating) {
    title = `Update needed: ${title}`;
  }

  const button = <button onClick={onPopoverToggle} className={classes} title={title} disabled={isDisabled} data-test-subj={`superDatePicker${position}DatePopoverButton`} {...buttonProps}>
      {formatTimeString(value, dateFormat, roundUp, locale)}
    </button>;
  return <EuiPopover button={button} isOpen={isOpen} closePopover={onPopoverClose} anchorPosition={position === 'start' ? 'downLeft' : 'downRight'} display="block" panelPaddingSize="none" ownFocus {...rest}>
      <EuiDatePopoverContent value={value} roundUp={roundUp} onChange={onChange} dateFormat={dateFormat} timeFormat={timeFormat} locale={locale} position={position} utcOffset={utcOffset} />
    </EuiPopover>;
};
EuiDatePopoverButton.propTypes = {
  className: PropTypes.string,
  buttonProps: PropTypes.any,
  dateFormat: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool,
  isInvalid: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  needsUpdating: PropTypes.bool,
  locale: PropTypes.any,
  onChange: PropTypes.any.isRequired,
  onPopoverClose: PropTypes.func.isRequired,
  onPopoverToggle: PropTypes.func.isRequired,
  position: PropTypes.oneOf(["start", "end"]).isRequired,
  roundUp: PropTypes.bool,
  timeFormat: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  utcOffset: PropTypes.number
};
EuiDatePopoverButton.displayName = 'EuiDatePopoverButton';

try {
  // @ts-ignore
  EuiDatePopoverButton.displayName = "EuiDatePopoverButton"; // @ts-ignore

  EuiDatePopoverButton.__docgenInfo = {
    "description": "",
    "displayName": "EuiDatePopoverButton",
    "props": {
      "className": {
        "defaultValue": null,
        "description": "",
        "name": "className",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "buttonProps": {
        "defaultValue": null,
        "description": "",
        "name": "buttonProps",
        "required": false,
        "type": {
          "name": "ButtonHTMLAttributes<HTMLButtonElement> | undefined"
        }
      },
      "dateFormat": {
        "defaultValue": null,
        "description": "",
        "name": "dateFormat",
        "required": true,
        "type": {
          "name": "string"
        }
      },
      "isDisabled": {
        "defaultValue": null,
        "description": "",
        "name": "isDisabled",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "isInvalid": {
        "defaultValue": null,
        "description": "",
        "name": "isInvalid",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "isOpen": {
        "defaultValue": null,
        "description": "",
        "name": "isOpen",
        "required": true,
        "type": {
          "name": "boolean"
        }
      },
      "needsUpdating": {
        "defaultValue": null,
        "description": "",
        "name": "needsUpdating",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "locale": {
        "defaultValue": null,
        "description": "",
        "name": "locale",
        "required": false,
        "type": {
          "name": "string | boolean | Moment | Duration | string[] | undefined"
        }
      },
      "onChange": {
        "defaultValue": null,
        "description": "",
        "name": "onChange",
        "required": true,
        "type": {
          "name": "(date: string | null, event?: SyntheticEvent<any, Event> | undefined) => void"
        }
      },
      "onPopoverClose": {
        "defaultValue": null,
        "description": "",
        "name": "onPopoverClose",
        "required": true,
        "type": {
          "name": "NoArgCallback<void>"
        }
      },
      "onPopoverToggle": {
        "defaultValue": null,
        "description": "",
        "name": "onPopoverToggle",
        "required": true,
        "type": {
          "name": "(event: MouseEvent<HTMLButtonElement, MouseEvent>) => void"
        }
      },
      "position": {
        "defaultValue": null,
        "description": "",
        "name": "position",
        "required": true,
        "type": {
          "name": "enum",
          "value": [{
            "value": "\"end\""
          }, {
            "value": "\"start\""
          }]
        }
      },
      "roundUp": {
        "defaultValue": null,
        "description": "",
        "name": "roundUp",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "timeFormat": {
        "defaultValue": null,
        "description": "",
        "name": "timeFormat",
        "required": true,
        "type": {
          "name": "string"
        }
      },
      "value": {
        "defaultValue": null,
        "description": "",
        "name": "value",
        "required": true,
        "type": {
          "name": "string"
        }
      },
      "utcOffset": {
        "defaultValue": null,
        "description": "",
        "name": "utcOffset",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/date_picker/super_date_picker/date_popover/date_popover_button.tsx#EuiDatePopoverButton"] = {
      docgenInfo: EuiDatePopoverButton.__docgenInfo,
      name: "EuiDatePopoverButton",
      path: "src/components/date_picker/super_date_picker/date_popover/date_popover_button.tsx#EuiDatePopoverButton"
    };
} catch (__react_docgen_typescript_loader_error) {}